// PIQ Advocate Engine - Opus 4.5 Build
// Overview Dashboard - CFO-grade KPIs and reporting

import React from 'react'
import { useLocation } from 'wouter'
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  MessageSquare,
  Award,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ExternalLink
} from 'lucide-react'
import { useAdvocateDashboard, useRecalculateScores, useProcessPayouts } from '../../lib/advocate-opus-data-service'

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdvocateOpusOverview() {
  const [, navigate] = useLocation()
  const { data: dashboard, isLoading, error, refetch } = useAdvocateDashboard(30)
  const recalculateScores = useRecalculateScores()
  const processPayouts = useProcessPayouts()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-gray-200 rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600">Error loading dashboard: {error.message}</p>
            <button 
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  const kpis = dashboard?.kpis
  const trends = dashboard?.trends || []
  const topAdvocates = dashboard?.top_advocates || []

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Advocate Engine</h1>
              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                Opus Build
              </span>
            </div>
            <p className="text-gray-500 mt-1">Commission-based advocate performance tracking</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => recalculateScores.mutate(undefined)}
              disabled={recalculateScores.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${recalculateScores.isPending ? 'animate-spin' : ''}`} />
              Recalculate Scores
            </button>
            <button
              onClick={() => processPayouts.mutate()}
              disabled={processPayouts.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Clock className={`w-4 h-4 ${processPayouts.isPending ? 'animate-spin' : ''}`} />
              Process Eligible Payouts
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-6 space-y-6">
        {/* CFO KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Net Revenue Attributed"
            value={`$${(kpis?.net_revenue_attributed || 0).toLocaleString()}`}
            subtitle={`Gross: $${(kpis?.gross_revenue_attributed || 0).toLocaleString()}`}
            icon={DollarSign}
            trend={kpis?.roi ? `${kpis.roi.toFixed(1)}% ROI` : undefined}
            trendUp={kpis?.roi ? kpis.roi > 0 : undefined}
          />
          <KPICard
            title="Total Commissions Paid"
            value={`$${(kpis?.total_commissions_paid || 0).toLocaleString()}`}
            subtitle={`Pending: $${(kpis?.pending_payout_liability || 0).toLocaleString()}`}
            icon={Award}
            trend={kpis?.avg_commission_rate ? `${kpis.avg_commission_rate.toFixed(1)}% avg rate` : undefined}
          />
          <KPICard
            title="Active Advocates"
            value={(kpis?.active_advocates || 0).toString()}
            subtitle={`${kpis?.new_advocates_30d || 0} new in 30d`}
            icon={Users}
            trend={`$${(kpis?.revenue_per_advocate || 0).toFixed(0)}/advocate`}
          />
          <KPICard
            title="CAC Equivalent"
            value={`$${(kpis?.cac_equivalent || 0).toFixed(2)}`}
            subtitle="Commission per new customer"
            icon={TrendingUp}
            trend={`${(kpis?.refund_rate || 0).toFixed(1)}% refund rate`}
            trendUp={kpis?.refund_rate ? kpis.refund_rate < 5 : undefined}
          />
        </div>

        {/* Tier Breakdown & Mentions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Advocate Tiers */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Advocate Tiers</h3>
            <div className="space-y-4">
              <TierBar 
                tier="A" 
                count={kpis?.tier_breakdown?.A || 0} 
                total={kpis?.total_advocates || 1}
                color="bg-green-500"
                description="Auto-invite (75-100)"
              />
              <TierBar 
                tier="B" 
                count={kpis?.tier_breakdown?.B || 0} 
                total={kpis?.total_advocates || 1}
                color="bg-yellow-500"
                description="Manual review (50-74)"
              />
              <TierBar 
                tier="C" 
                count={kpis?.tier_breakdown?.C || 0} 
                total={kpis?.total_advocates || 1}
                color="bg-gray-400"
                description="Monitor (0-49)"
              />
            </div>
            <button
              onClick={() => navigate('/advocate-opus/discovery')}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
            >
              View All Advocates
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>

          {/* Mentions Summary */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Social Mentions</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Mentions</span>
                <span className="font-semibold">{(kpis?.total_mentions || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Last 30 Days</span>
                <span className="font-semibold">{(kpis?.mentions_30d || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Positive Sentiment</span>
                <span className="font-semibold text-green-600">{(kpis?.positive_mentions_pct || 0).toFixed(1)}%</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/advocate-opus/discovery?status=pending')}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
            >
              <MessageSquare className="w-4 h-4" />
              Review New Mentions
            </button>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <ActionButton
                label="View Pending Payouts"
                count={kpis?.pending_payout_liability ? `$${kpis.pending_payout_liability.toLocaleString()}` : undefined}
                onClick={() => navigate('/advocate-opus/payouts?status=pending')}
              />
              <ActionButton
                label="Manage Offers"
                onClick={() => navigate('/advocate-opus/offers')}
              />
              <ActionButton
                label="Export Payout Report"
                onClick={() => navigate('/advocate-opus/payouts?action=export')}
              />
            </div>
          </div>
        </div>

        {/* Top Advocates Table */}
        <div className="bg-white rounded-xl border">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Top Performing Advocates</h3>
            <button
              onClick={() => navigate('/advocate-opus/discovery?tier=A')}
              className="text-sm text-purple-600 hover:text-purple-700"
            >
              View All →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advocate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commissions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {topAdvocates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No advocates yet. Start by ingesting mentions.
                    </td>
                  </tr>
                ) : (
                  topAdvocates.map((adv) => (
                    <tr 
                      key={adv.advocate_id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/advocate-opus/advocates/${adv.advocate_id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">@{adv.primary_handle}</div>
                        {adv.display_name && (
                          <div className="text-sm text-gray-500">{adv.display_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="capitalize text-gray-600">{adv.primary_platform}</span>
                      </td>
                      <td className="px-6 py-4">
                        <TierBadge tier={adv.tier} />
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        {adv.score.toFixed(0)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-green-600">
                        ${adv.total_revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {adv.total_conversions}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-600">
                        ${adv.total_commissions.toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface KPICardProps {
  title: string
  value: string
  subtitle?: string
  icon: React.ElementType
  trend?: string
  trendUp?: boolean
}

function KPICard({ title, value, subtitle, icon: Icon, trend, trendUp }: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-purple-600" />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          trendUp === true ? 'text-green-600' : trendUp === false ? 'text-red-600' : 'text-gray-600'
        }`}>
          {trendUp === true && <ArrowUpRight className="w-4 h-4" />}
          {trendUp === false && <ArrowDownRight className="w-4 h-4" />}
          {trend}
        </div>
      )}
    </div>
  )
}

interface TierBarProps {
  tier: string
  count: number
  total: number
  color: string
  description: string
}

function TierBar({ tier, count, total, color, description }: TierBarProps) {
  const percentage = total > 0 ? (count / total) * 100 : 0
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <TierBadge tier={tier} />
          <span className="text-sm text-gray-500">{description}</span>
        </div>
        <span className="font-semibold">{count}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  )
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-700',
    B: 'bg-yellow-100 text-yellow-700',
    C: 'bg-gray-100 text-gray-700',
  }
  
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded ${colors[tier] || colors.C}`}>
      {tier}
    </span>
  )
}

interface ActionButtonProps {
  label: string
  count?: string
  onClick: () => void
}

function ActionButton({ label, count, onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-3 text-sm text-left border rounded-lg hover:bg-gray-50"
    >
      <span>{label}</span>
      {count && <span className="font-semibold text-purple-600">{count}</span>}
    </button>
  )
}
